/**
 * An institution proposal's answer (TMMAPP-0163 item 12; TMMAPI-0195 U2 item 8): tmm-api's matching
 * job found the catalog institution an account's typed bank name stands for, and asks the owner. It's
 * never applied without them: **Yes** confirms it (`…/institution/confirm`, with the id shown, so a
 * stale screen never confirms another), **No** dismisses it (`…/institution/dismiss`), and the caller
 * re-reads after either answer.
 *
 * A Yes writes nothing else (TMMAPP-0165 item 17): the account's served `institutionCatalog` name
 * shows wherever the institution does (`institutionLabel`), and the owner's typed name is left as
 * they wrote it.
 *
 * Both answers are writes, so a GUEST or NONE tier gets the subscribe offer
 * (`useSubscriberAction()`), never a disabled control.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account, type InstitutionProposal } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import { useSubscriberAction } from '@/session/useSubscriberAction';

/** The account's standing proposal, or null (none, or not the owner's full view). */
export function proposalOf(account: Account): InstitutionProposal | null {
  return account.institutionProposal ?? null;
}

/**
 * What a standing proposal adds to an account's review count: 1 on a managed account, else 0. tmm-api's
 * `byAccount` doesn't count it (it isn't a review item), so the account's tab and its card both add
 * this to the served count: one rule (TMMAPP-0165 item 18).
 */
export function proposalReviewCount(account: Account): number {
  return account.management !== 'UNMANAGED' && proposalOf(account) !== null ? 1 : 0;
}

export interface InstitutionAnswerOptions {
  /** After an answer, success or not: the caller re-reads. */
  onAnswered: () => void;
  onNotify: (message: string) => void;
}

/** Yes and No for one account's proposal, through the subscriber gate. */
export function useInstitutionAnswer(
  account: Account,
  proposal: InstitutionProposal,
  { onAnswered, onNotify }: InstitutionAnswerOptions,
) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function answer(yes: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      if (yes) {
        await accountsApi.confirmInstitution(account.id, proposal.institutionId);
        onNotify(t('institutions.proposal.confirmed', { name: proposal.name }));
      } else {
        await accountsApi.dismissInstitution(account.id);
        onNotify(t('institutions.proposal.dismissed'));
      }
    } catch (error) {
      onNotify(errorCopy(t, error, 'accounts.errorGeneric'));
    } finally {
      setBusy(false);
      onAnswered();
    }
  }

  const yes = useSubscriberAction(() => void answer(true));
  const no = useSubscriberAction(() => void answer(false));
  return { busy, yes, no };
}
